import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, MapPin, CreditCard, Smartphone, Truck, Plus, Check, X, CheckCircle2, XCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { cart, clearCart, addToCart } = useCart();

  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('upi');
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingAddresses, setFetchingAddresses] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ open: false, url: '', orderId: '', status: 'pending' });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const pollRef = useRef(null);

  const [newAddress, setNewAddress] = useState({
    full_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
    is_default: false
  });

  const discount = location.state?.discount || 0;
  const appliedCoupon = location.state?.coupon || null;

  const fetchAddresses = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setFetchingAddresses(false);
      return;
    }

    try {
      setFetchingAddresses(true);
      const response = await fetch(`${API}/addresses`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAddresses(data);
        const defaultAddr = data.find(a => a.is_default) || data[0];
        if (defaultAddr) setSelectedAddress(defaultAddr.address_id);
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
    } finally {
      setFetchingAddresses(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/checkout');
      return;
    }

    const processPendingCheckout = async () => {
      const pending = localStorage.getItem('pendingCheckout');
      if (pending) {
        try {
          const { productId, quantity } = JSON.parse(pending);
          await addToCart(productId, quantity);
          localStorage.removeItem('pendingCheckout');
        } catch (error) {
          console.error('Failed to process pending checkout:', error);
        }
      }
    };

    if (cart.items.length === 0) {
      const pending = localStorage.getItem('pendingCheckout');
      if (!pending) {
        navigate('/cart');
        return;
      }
      processPendingCheckout();
    }
    if (!authLoading && user) {
      fetchAddresses();
    }
  }, [user, authLoading, cart.items.length, navigate, fetchAddresses, addToCart]);

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify(newAddress)
      });

      if (!response.ok) throw new Error('Failed to add address');

      const data = await response.json();
      await fetchAddresses();
      setSelectedAddress(data.address_id);
      setShowAddAddress(false);
      setNewAddress({
        full_name: '',
        phone: '',
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        pincode: '',
        is_default: false
      });
      toast.success('Address added successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Stop polling + cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  /**
   * Polls backend every 5s for payment status.
   * Backend receives the NeoPayOut webhook → updates order status in DB.
   * We detect the change here and respond.
   */
  const startPollingPaymentStatus = (orderId) => {
    stopPolling();
    const token = localStorage.getItem('token');
    const check = async () => {
      try {
        const res = await fetch(`${API}/orders/${orderId}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          credentials: 'include'
        });
        if (!res.ok) return;
        const data = await res.json();
        const pStatus = (data.payment_status || '').toLowerCase();
        const oStatus = (data.status || '').toLowerCase();
        if (pStatus === 'paid' || pStatus === 'success' || oStatus === 'confirmed' || oStatus === 'processing') {
          stopPolling();
          setPaymentModal(prev => ({ ...prev, status: 'success' }));
          clearCart();
          setTimeout(() => {
            setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
            navigate(`/order-success/${orderId}`);
          }, 2500);
        } else if (pStatus === 'failed' || pStatus === 'cancelled' || oStatus === 'cancelled') {
          stopPolling();
          setPaymentModal(prev => ({ ...prev, status: 'failed' }));
        }
      } catch { }
    };
    pollRef.current = setInterval(check, 5000);
  };

  /**
   * Manual check — triggered when user clicks "I've completed payment".
   * Instantly polls backend once and shows result.
   */
  const manualCheckStatus = async () => {
    if (checkingStatus) return;
    setCheckingStatus(true);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/orders/${paymentModal.orderId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const pStatus = (data.payment_status || '').toLowerCase();
      const oStatus = (data.status || '').toLowerCase();
      if (pStatus === 'paid' || pStatus === 'success' || oStatus === 'confirmed' || oStatus === 'processing') {
        stopPolling();
        setPaymentModal(prev => ({ ...prev, status: 'success' }));
        clearCart();
        setTimeout(() => {
          setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
          navigate(`/order-success/${paymentModal.orderId}`);
        }, 2500);
      } else if (pStatus === 'failed' || pStatus === 'cancelled') {
        stopPolling();
        setPaymentModal(prev => ({ ...prev, status: 'failed' }));
      } else {
        toast.info('Payment not confirmed yet. Please complete payment and try again.');
      }
    } catch {
      toast.error('Could not check payment status. Please wait a moment.');
    } finally {
      setCheckingStatus(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      toast.error('Please select a delivery address');
      return;
    }

    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty. Please add items before placing an order.');
      return;
    }

    setProcessingPayment(true);
    const token = localStorage.getItem('token');

    try {
      // Step 1: Sync frontend cart items to backend before placing order
      await fetch(`${API}/cart/clear`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        credentials: 'include'
      });

      for (const item of cart.items) {
        await fetch(`${API}/cart/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
          credentials: 'include',
          body: JSON.stringify({ product_id: item.product_id, quantity: item.quantity })
        });
      }

      // Step 2: Create order
      const orderResponse = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ address_id: selectedAddress, payment_method: paymentMethod, coupon_code: appliedCoupon })
      });

      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.detail || 'Failed to create order');
      }

      const orderData = await orderResponse.json();

      if (paymentMethod === 'cod') {
        clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-success/${orderData.order_id}`);
      } else if (orderData.payment_url) {
        // Open payment in modal instead of redirecting
        setPaymentModal({ open: true, url: orderData.payment_url, orderId: orderData.order_id, status: 'pending' });
        startPollingPaymentStatus(orderData.order_id);
      } else {
        const msg = orderData.payment_init_message || 'Payment details could not be initialized. Please try again or contact support.';
        throw new Error(msg);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessingPayment(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://via.placeholder.com/60';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const subtotal = cart.total;
  const shipping = (subtotal > 999 || subtotal <= 10) ? 0 : 99;
  
  // Calculate inclusive GST
  const totalTax = cart.items.reduce((sum, item) => {
    const rate = item.product?.igst || 18;
    const taxValue = item.item_total - (item.item_total / (1 + rate / 100));
    return sum + taxValue;
  }, 0);

  // Determine state-based breakdown
  const businessState = 'Odisha';
  const selectedAddrObj = addresses.find(a => a.address_id === selectedAddress);
  const isSameState = selectedAddrObj?.state?.toLowerCase() === businessState.toLowerCase();

  const total = subtotal - discount + shipping;

  return (
    <div className="min-h-screen bg-white" data-testid="checkout-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/cart')}
            className="p-2 hover:bg-surface rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-3xl md:text-4xl text-secondary">Checkout</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Delivery Address */}
            <div className="bg-surface rounded-3xl p-6" data-testid="address-section">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h2 className="font-heading text-xl text-secondary">Delivery Address</h2>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddAddress(!showAddAddress)}
                  className="rounded-full"
                  data-testid="add-address-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add New
                </Button>
              </div>

              {/* Add Address Form */}
              {showAddAddress && (
                <form onSubmit={handleAddAddress} className="bg-white rounded-2xl p-6 mb-6 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={newAddress.full_name}
                        onChange={(e) => setNewAddress({ ...newAddress, full_name: e.target.value })}
                        required
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={newAddress.phone}
                        onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                        required
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      value={newAddress.address_line1}
                      onChange={(e) => setNewAddress({ ...newAddress, address_line1: e.target.value })}
                      required
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label>Address Line 2 (Optional)</Label>
                    <Input
                      value={newAddress.address_line2}
                      onChange={(e) => setNewAddress({ ...newAddress, address_line2: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                        required
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={newAddress.state}
                        onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                        required
                        className="mt-1 rounded-xl"
                      />
                    </div>
                    <div>
                      <Label>Pincode</Label>
                      <Input
                        value={newAddress.pincode}
                        onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })}
                        required
                        className="mt-1 rounded-xl"
                      />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Button type="submit" disabled={loading} className="rounded-full bg-primary">
                      {loading ? 'Saving...' : 'Save Address'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddAddress(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              {/* Address List */}
              <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress}>
                <div className="space-y-3">
                  {addresses.map((addr) => (
                    <label
                      key={addr.address_id}
                      className={`flex items-start gap-4 p-4 rounded-2xl cursor-pointer transition-all ${selectedAddress === addr.address_id
                        ? 'bg-primary/5 border-2 border-primary'
                        : 'bg-white border-2 border-transparent hover:border-gray-200'
                        }`}
                      data-testid={`address-${addr.address_id}`}
                    >
                      <RadioGroupItem value={addr.address_id} className="mt-1" />
                      <div className="flex-1">
                        <p className="font-body font-semibold text-secondary">{addr.full_name}</p>
                        <p className="text-sm text-muted-foreground font-body mt-1">
                          {addr.address_line1}
                          {addr.address_line2 && `, ${addr.address_line2}`}
                        </p>
                        <p className="text-sm text-muted-foreground font-body">
                          {addr.city}, {addr.state} - {addr.pincode}
                        </p>
                        <p className="text-sm text-muted-foreground font-body mt-1">
                          Phone: {addr.phone}
                        </p>
                      </div>
                      {addr.is_default && (
                        <span className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full font-body">
                          Default
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </RadioGroup>

              {fetchingAddresses ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary opacity-50" />
                  <p className="text-slate-400 text-sm font-body">Loading your saved addresses...</p>
                </div>
              ) : (
                <>
                  {addresses.length === 0 && !showAddAddress && (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground font-body mb-4">No addresses saved yet</p>
                      <Button
                        onClick={() => setShowAddAddress(true)}
                        className="rounded-full bg-primary"
                      >
                        Add Your First Address
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Method */}
            <div className="bg-surface rounded-3xl p-6" data-testid="payment-section">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl text-secondary">Payment Method</h2>
              </div>

              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="space-y-3">
                  <label
                    className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'upi'
                      ? 'bg-primary/5 border-2 border-primary'
                      : 'bg-white border-2 border-transparent hover:border-gray-200'
                      }`}
                    data-testid="payment-upi"
                  >
                    <RadioGroupItem value="upi" />
                    <Smartphone className="w-5 h-5 text-secondary" />
                    <div className="flex-1">
                      <p className="font-body font-semibold text-secondary">UPI</p>
                      <p className="text-sm text-muted-foreground font-body">Google Pay, PhonePe, Paytm</p>
                    </div>
                  </label>

                  {/* 
                  <label
                    className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'card'
                      ? 'bg-primary/5 border-2 border-primary'
                      : 'bg-white border-2 border-transparent hover:border-gray-200'
                      }`}
                    data-testid="payment-card"
                  >
                    <RadioGroupItem value="card" />
                    <CreditCard className="w-5 h-5 text-secondary" />
                    <div className="flex-1">
                      <p className="font-body font-semibold text-secondary">Credit / Debit Card</p>
                      <p className="text-sm text-muted-foreground font-body">All major cards accepted</p>
                    </div>
                  </label>
*/}

                  <label
                    className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'cod'
                      ? 'bg-primary/5 border-2 border-primary'
                      : 'bg-white border-2 border-transparent hover:border-gray-200'
                      }`}
                    data-testid="payment-cod"
                  >
                    <RadioGroupItem value="cod" />
                    <Truck className="w-5 h-5 text-secondary" />
                    <div className="flex-1">
                      <p className="font-body font-semibold text-secondary">Cash on Delivery</p>
                      <p className="text-sm text-muted-foreground font-body">Pay when you receive</p>
                    </div>
                  </label>
                </div>
              </RadioGroup>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-3xl p-6 sticky top-24" data-testid="order-summary">
              <h2 className="font-heading text-xl text-secondary mb-6">Order Summary</h2>

              {/* Items Preview */}
              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {cart.items.map((item) => (
                  <div key={item.product_id} className="flex gap-3">
                    <img
                      src={getImageUrl(item.product?.images?.[0])}
                      alt={item.product?.name}
                      className="w-14 h-14 rounded-xl object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-body text-secondary line-clamp-1">
                        {item.product?.name}
                      </p>
                      <p className="text-xs text-muted-foreground font-body">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <p className="text-sm font-body font-medium text-secondary">
                      {formatPrice(item.item_total)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="space-y-3 text-sm font-body border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-secondary font-medium">{formatPrice(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-success">
                    <span>Discount ({appliedCoupon})</span>
                    <span>-{formatPrice(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="text-secondary font-medium">
                    {shipping === 0 ? 'FREE' : formatPrice(shipping)}
                  </span>
                </div>

                {/* Tax Breakdown */}
                <div className="pt-2 mt-2 border-t border-dashed border-gray-100 flex flex-col gap-1.5">
                  {isSameState ? (
                    <>
                      <div className="flex justify-between text-[11px] text-muted-foreground italic">
                        <span>CGST (Included)</span>
                        <span>{formatPrice(totalTax / 2)}</span>
                      </div>
                      <div className="flex justify-between text-[11px] text-muted-foreground italic">
                        <span>SGST (Included)</span>
                        <span>{formatPrice(totalTax / 2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between text-[11px] text-muted-foreground italic">
                      <span>IGST (Included)</span>
                      <span>{formatPrice(totalTax)}</span>
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold text-secondary">Total</span>
                    <span className="font-semibold text-secondary text-lg">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>

              {/* Place Order Button */}
              {addresses.length === 0 && !fetchingAddresses ? (
                <Button
                  onClick={() => {
                    setShowAddAddress(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    toast.error('Please add a delivery address to proceed');
                  }}
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press"
                  data-testid="add-address-prompt-btn"
                >
                  Add Delivery Address
                </Button>
              ) : (
                <Button
                  onClick={handlePlaceOrder}
                  disabled={processingPayment || fetchingAddresses}
                  className="w-full mt-6 bg-primary hover:bg-primary/90 text-white rounded-full py-6 font-body btn-press"
                  data-testid="place-order-btn"
                >
                  {processingPayment ? 'Processing...' : `Place Order - ${formatPrice(total)}`}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── PAYMENT MODAL ─────────────────────────────────────── */}
      {paymentModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
          {/* Blurred backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" />

          {/* Modal card */}
          <div
            className="relative w-full sm:max-w-lg bg-white sm:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col overflow-hidden"
            style={{ maxHeight: '92vh' }}
          >
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-slate-900 to-blue-950 text-white flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-2xl bg-emerald-400/15 border border-emerald-400/30 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-semibold text-sm leading-tight">Secure Checkout</p>
                  <p className="text-white/40 text-[11px] mt-0.5">Order #{paymentModal.orderId}</p>
                </div>
              </div>
              {/* Only allow close when pending (not success/failed auto-close) */}
              {paymentModal.status === 'pending' && (
                <button
                  onClick={() => {
                    stopPolling();
                    setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
                    toast('Payment window closed. Your order is saved — you can retry from My Orders.', { icon: 'ℹ️' });
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* ── SUCCESS SCREEN ── */}
            {paymentModal.status === 'success' && (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center flex-1">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-emerald-50 flex items-center justify-center ring-[10px] ring-emerald-100 mb-6">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500" />
                  </div>
                  {/* Pulse ring animation */}
                  <span className="absolute inset-0 rounded-full animate-ping bg-emerald-200 opacity-30" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mt-2 mb-2">Payment Successful! 🎉</h2>
                <p className="text-gray-400 text-sm mb-8 max-w-xs leading-relaxed">
                  Your order is confirmed and will be dispatched shortly.
                </p>
                <div className="flex items-center gap-2 text-sm text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 px-5 py-2.5 rounded-full">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to order details...
                </div>
              </div>
            )}

            {/* ── FAILED SCREEN ── */}
            {paymentModal.status === 'failed' && (
              <div className="flex flex-col items-center justify-center py-16 px-8 text-center flex-1">
                <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center ring-[10px] ring-red-100 mb-6">
                  <XCircle className="w-12 h-12 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Failed</h2>
                <p className="text-gray-400 text-sm mb-8 max-w-xs leading-relaxed">
                  Your payment could not be processed. You can retry using the same or a different method.
                </p>
                <div className="flex gap-3 flex-wrap justify-center">
                  <button
                    onClick={() => {
                      setPaymentModal(prev => ({ ...prev, status: 'pending' }));
                      startPollingPaymentStatus(paymentModal.orderId);
                    }}
                    className="px-8 py-3 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm active:scale-95"
                  >
                    Retry Payment
                  </button>
                  <button
                    onClick={() => {
                      stopPolling();
                      setPaymentModal({ open: false, url: '', orderId: '', status: 'pending' });
                    }}
                    className="px-8 py-3 border-2 border-gray-200 text-gray-500 rounded-full text-sm font-semibold hover:bg-gray-50 transition-all active:scale-95"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* ── PAYMENT IFRAME (pending state) ── */}
            {paymentModal.status === 'pending' && (
              <>
                {/* Scrollable iframe — clean, no status bar inside */}
                <div className="overflow-y-auto flex-1" style={{ minHeight: '420px' }}>
                  <iframe
                    src={paymentModal.url}
                    title="Payment Gateway"
                    className="w-full border-0"
                    style={{ minHeight: '560px', display: 'block' }}
                    allow="payment"
                  />
                </div>

                {/* ── Action footer — OUTSIDE the iframe ── */}
                <div className="flex-shrink-0 border-t border-gray-100 bg-gray-50 px-5 py-4">
                  <p className="text-center text-[11px] text-gray-400 mb-3">
                    After completing payment on the screen above, tap the button below.
                  </p>
                  <button
                    onClick={manualCheckStatus}
                    disabled={checkingStatus}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white rounded-2xl py-3.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                  >
                    {checkingStatus ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Checking payment status...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        I've completed payment
                      </>
                    )}
                  </button>
                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-3 mt-3">
                    <span className="flex items-center gap-1 text-[10px] text-gray-400">
                      <ShieldCheck className="w-3 h-3 text-emerald-500" /> 256-bit SSL
                    </span>
                    <span className="text-gray-200 text-xs">•</span>
                    <span className="text-[10px] text-gray-400">PCI DSS Compliant</span>
                    <span className="text-gray-200 text-xs">•</span>
                    <span className="text-[10px] text-gray-400">Powered by NeoPayOut</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
